import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ChevronDown, Search, ArrowLeft, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LANGUAGES } from "@/data/languages";
import { BRAND } from "@/config/brand";

const GENDERS = ["man", "woman", "non-binary"];
const PREFS = ["men", "women", "everyone"];
const STEPS = ["dob", "gender", "match_preference", "primary_language", "secondary_language"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, setUser, loading, logout } = useAuth();
  const [hydrated, setHydrated] = useState(false);
  const [state, setState] = useState({ dob: "", gender: "", match_preference: "", primary_language: "", secondary_language: "" });
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [langQ, setLangQ] = useState("");
  const submitted = React.useRef(false);

  useEffect(() => {
    if (loading || !user) return;
    (async () => {
      try {
        const res = await api.get("/users/me/onboarding");
        const s = res.data.onboarding_state || {};
        const merged = { dob: s.dob || "", gender: s.gender || "", match_preference: s.match_preference || "", primary_language: s.primary_language || "", secondary_language: s.secondary_language || "" };
        setState(merged);
        let resume = 0;
        for (let i = 0; i < STEPS.length; i++) { if (merged[STEPS[i]]) resume = i + 1; else break; }
        setStep(Math.min(resume, STEPS.length - 1));
      } catch { /* interactive anyway */ } finally { setHydrated(true); }
    })();
  }, [user, loading]);

  const persist = useCallback((patch) => { api.patch("/users/me/onboarding", patch).catch(() => {}); }, []);

  const submitAll = useCallback(async (full) => {
    if (submitted.current) return;
    submitted.current = true;
    setSaving(true);
    try {
      const res = await api.post("/users/me/onboarding", {
        gender: full.gender, match_preference: full.match_preference,
        primary_language: full.primary_language, secondary_language: full.secondary_language || null,
        dob: full.dob, adult_confirmation: true,
      });
      setUser(res.data);
      navigate("/app/home", { replace: true });
    } catch (e) {
      submitted.current = false; setSaving(false);
      toast.error(e?.response?.data?.detail || "Couldn't save. Please try again.");
    }
  }, [navigate, setUser]);

  const choose = (field, value) => {
    const next = { ...state, [field]: value };
    setState(next);
    persist({ [field]: value });
    const idx = STEPS.indexOf(field);
    if (idx < STEPS.length - 1) setStep(idx + 1);
    else submitAll(next);
  };

  const goBack = () => step > 0 && setStep(step - 1);
  const doSignOut = async () => { await logout(); navigate("/", { replace: true }); };
  const filteredLangs = LANGUAGES.filter((l) => l.toLowerCase().includes(langQ.toLowerCase()));

  if (!hydrated || loading || saving) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#0A0908] bg-noise">
        <div className="flex gap-2"><span className="dot h-2 w-2 rounded-full bg-[#D4AF37]" /><span className="dot h-2 w-2 rounded-full bg-[#D4AF37]" /><span className="dot h-2 w-2 rounded-full bg-[#D4AF37]" /></div>
        {saving && <p className="text-sm text-[#C8C2B5]">Saving your preferences…</p>}
      </div>
    );
  }

  const Option = ({ label, active, onClick, testid }) => (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`w-full rounded-xl border px-5 py-4 text-left capitalize transition-all ${active ? "border-[#D4AF37] bg-[#24201C] text-[#F4F1EA]" : "border-[#3D3428] bg-[#12100E] text-[#C8C2B5] hover:border-[#5a4d38]"}`}>
      <span className="flex items-center justify-between">{label}{active && <Check className="h-4 w-4 text-[#D4AF37]" />}</span>
    </button>
  );

  const meta = [
    { title: "When were you born?", sub: "Kept private for age verification. Never shown to anyone." },
    { title: "How do you identify?", sub: "This helps others and personas speak to you naturally." },
    { title: "Who would you like to meet?", sub: "Applies to future matches. You can change it anytime." },
    { title: "Which language feels like home?", sub: "Your primary conversation language." },
    { title: "Add a second language? (optional)", sub: "Used only to widen matching when needed." },
  ];

  return (
    <div className="min-h-screen bg-[#0A0908] bg-noise px-5 py-10" data-testid="onboarding-wizard">
      <div className="mx-auto max-w-md">
        <div className="mb-8 flex items-center justify-between">
          <span className="font-display text-2xl text-gold-gradient">{BRAND.name}</span>
          <span className="text-xs text-[#8C8578]">Step {step + 1} of {STEPS.length}</span>
        </div>
        <div className="mb-8 h-1 w-full overflow-hidden rounded-full bg-[#24201C]">
          <motion.div className="h-full bg-[#D4AF37]" animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }} transition={{ duration: 0.4 }} />
        </div>

        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
          <h1 className="font-display text-3xl font-medium tracking-tight text-[#F4F1EA]">{meta[step].title}</h1>
          <p className="mb-6 mt-2 text-sm text-[#C8C2B5]">{meta[step].sub}</p>

          {step === 0 && (
            <div className="space-y-4">
              <input type="date" data-testid="dob-input" value={state.dob} max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setState({ ...state, dob: e.target.value })}
                className="w-full rounded-xl border border-[#3D3428] bg-[#12100E] px-5 py-4 text-[#F4F1EA] outline-none focus:border-[#D4AF37]/60" />
              <Button data-testid="dob-continue" disabled={!state.dob} onClick={() => choose("dob", state.dob)}
                className="w-full rounded-full bg-[#D4AF37] py-6 font-semibold text-[#0A0908] hover:bg-[#E5C158]">Continue</Button>
            </div>
          )}
          {step === 1 && <div className="space-y-3">{GENDERS.map((g) => <Option key={g} label={g} active={state.gender === g} testid={`gender-${g.replace(/[^a-z]/g, "-")}`} onClick={() => choose("gender", g)} />)}</div>}
          {step === 2 && <div className="space-y-3">{PREFS.map((p) => <Option key={p} label={p} active={state.match_preference === p} testid={`pref-${p}`} onClick={() => choose("match_preference", p)} />)}</div>}
          {(step === 3 || step === 4) && (
            <div className="space-y-3">
              <Popover open={langOpen} onOpenChange={setLangOpen}>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="language-select-trigger" className="flex w-full items-center justify-between rounded-xl border border-[#3D3428] bg-[#12100E] px-5 py-4 text-left text-[#F4F1EA]">
                    {(step === 3 ? state.primary_language : state.secondary_language) || "Select a language"}
                    <ChevronDown className="h-4 w-4 text-[#8C8578]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] border-[#3D3428] bg-[#12100E] p-0" align="start">
                  <div className="flex items-center gap-2 border-b border-[#3D3428] px-3 py-2"><Search className="h-4 w-4 text-[#8C8578]" /><input autoFocus value={langQ} onChange={(e) => setLangQ(e.target.value)} placeholder="Search languages" className="w-full bg-transparent text-sm text-[#F4F1EA] outline-none placeholder:text-[#8C8578]" /></div>
                  <div className="max-h-60 overflow-y-auto py-1">
                    {filteredLangs.filter((l) => step === 3 || l !== state.primary_language).map((l) => (
                      <button key={l} type="button" data-testid={`language-option-${l.toLowerCase().replace(/[^a-z]/g, "-")}`}
                        onClick={() => { setLangOpen(false); setLangQ(""); choose(step === 3 ? "primary_language" : "secondary_language", l); }}
                        className="flex w-full items-center justify-between px-4 py-2 text-left text-sm text-[#C8C2B5] hover:bg-[#24201C]">{l}</button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              {step === 4 && <Button data-testid="skip-secondary" variant="outline" onClick={() => submitAll({ ...state, secondary_language: "" })} className="w-full rounded-full border-[#3D3428] bg-transparent py-5 text-[#C8C2B5] hover:bg-[#1A1714]">Skip — no second language</Button>}
            </div>
          )}
        </motion.div>

        <div className="mt-8">
          {step === 0 ? (
            <button data-testid="onboarding-signout" onClick={doSignOut} className="flex items-center gap-1 text-sm text-[#8C8578] hover:text-[#C8C2B5]"><LogOut className="h-4 w-4" /> Sign out</button>
          ) : (
            <button data-testid="onboarding-back" onClick={goBack} className="flex items-center gap-1 text-sm text-[#8C8578] hover:text-[#C8C2B5]"><ArrowLeft className="h-4 w-4" /> Back</button>
          )}
        </div>
      </div>
    </div>
  );
}
